import { useState } from 'react';
import { ArrowLeft, Plus, Pencil, Trash2, GripVertical } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useCamposTipoAtivo, useDeleteCampoTipoAtivo } from '@/hooks/useCamposTipoAtivo';
import { TipoAtivo, CampoTipoAtivo } from '@/types/tipo-ativo';
import { NewCampoTipoAtivoDialog } from './NewCampoTipoAtivoDialog';
import { EditCampoTipoAtivoDialog } from './EditCampoTipoAtivoDialog';

interface CamposTipoAtivoManagerProps {
  tipoAtivo: TipoAtivo;
  onBack: () => void;
}

const tabLabels = {
  basico: 'Básico',
  tecnico: 'Técnico',
  negocio: 'Negócio',
  seguranca: 'Segurança',
};

const tipoCampoLabels: Record<string, string> = {
  text: 'Texto',
  textarea: 'Texto Longo',
  number: 'Número',
  date: 'Data',
  boolean: 'Sim/Não',
  select: 'Lista',
  multiselect: 'Múltipla Escolha',
  email: 'E-mail',
  url: 'URL',
};

export const CamposTipoAtivoManager = ({ tipoAtivo, onBack }: CamposTipoAtivoManagerProps) => {
  const [newDialogOpen, setNewDialogOpen] = useState(false);
  const [editingCampo, setEditingCampo] = useState<CampoTipoAtivo | null>(null);

  const { data: campos, isLoading } = useCamposTipoAtivo(tipoAtivo.id);
  const deleteCampoMutation = useDeleteCampoTipoAtivo();

  const handleDelete = async (campo: CampoTipoAtivo) => {
    if (confirm(`Tem certeza que deseja deletar o campo "${campo.label}"?`)) {
      deleteCampoMutation.mutate({ id: campo.id, tipoAtivoId: tipoAtivo.id });
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={onBack}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold">Campos Customizados</h1>
            <p className="text-muted-foreground">
              Gerenciar campos para: {tipoAtivo.nome}
            </p>
          </div>
        </div>
        <Button onClick={() => setNewDialogOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Novo Campo
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Lista de Campos</CardTitle>
          <CardDescription>
            {campos?.length || 0} campos customizados configurados
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center py-8 text-muted-foreground">
              Carregando campos...
            </div>
          ) : !campos || campos.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              Nenhum campo customizado. Clique em "Novo Campo" para adicionar.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12"></TableHead>
                  <TableHead>Label</TableHead>
                  <TableHead>Nome do Campo</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Tab</TableHead>
                  <TableHead>Obrigatório</TableHead>
                  <TableHead>Ordem</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {campos.map((campo) => (
                  <TableRow key={campo.id}>
                    <TableCell>
                      <GripVertical className="h-4 w-4 text-muted-foreground cursor-move" />
                    </TableCell>
                    <TableCell className="font-medium">{campo.label}</TableCell>
                    <TableCell>
                      <code className="text-sm bg-muted px-1 py-0.5 rounded">
                        {campo.nome_campo}
                      </code>
                    </TableCell>
                    <TableCell>{tipoCampoLabels[campo.tipo_campo]}</TableCell>
                    <TableCell>
                      <Badge variant="secondary">
                        {tabLabels[campo.tab]}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {campo.obrigatorio ? (
                        <Badge variant="destructive">Sim</Badge>
                      ) : (
                        <Badge variant="outline">Não</Badge>
                      )}
                    </TableCell>
                    <TableCell>{campo.ordem}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setEditingCampo(campo)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(campo)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <NewCampoTipoAtivoDialog
        tipoAtivoId={tipoAtivo.id}
        open={newDialogOpen}
        onOpenChange={setNewDialogOpen}
      />

      {editingCampo && (
        <EditCampoTipoAtivoDialog
          campo={editingCampo}
          tipoAtivoId={tipoAtivo.id}
          open={!!editingCampo}
          onOpenChange={(open) => !open && setEditingCampo(null)}
        />
      )}
    </div>
  );
};
